///
/// EarthBrowser
///
/// Created by Matt Giger on 10/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Diffuse Milky Way behind the stars: an equirectangular map in
/// equatorial coordinates (NASA SVS "Deep Star Maps 2020", diffuse galaxy
/// layer with the catalog stars removed, since those are drawn from the
/// Hipparcos data) sampled per fragment along the view ray.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { trig } from "@stovokor/math/math"
import { cross, normal, vec2, type vec3 } from "@stovokor/math/vector"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { Perspective, cCameraFOV } from "@stovokor/camera/Perspective"
import { type SkyEphemeris } from "./Sky"
import { FullscreenTriangle, cSkyOrderMilkyWay } from "./SkyGeometry"

const cMilkyWayTextureUrl = "/images/shaders/milkyway.jpg"
// Overall brightness of the band relative to the source map
const cMilkyWayIntensity = 0.3

export class MilkyWay extends NodeBase {
	private material: MilkyWayMaterial

	constructor(context: StovokorContext, ephemeris: SkyEphemeris) {
		super()
		this.renderOrder = cSkyOrderMilkyWay
		this.material = new MilkyWayMaterial(context)
		this.renderer = new MilkyWayRenderer(context, new FullscreenTriangle(context), this.material, ephemeris)
	}

	public get textureLoaded(): boolean {
		return this.material.available
	}

	override destroy(): void {
		this.material.milkyWayTexture.destroy()
		super.destroy()
	}
}

class MilkyWayMaterial implements Material {
	public transparency: boolean = false
	public milkyWayTexture: Texture

	constructor(context: StovokorContext) {
		// Wrap horizontally so the right-ascension seam filters cleanly
		this.milkyWayTexture = new Texture(context, 1, 1,
			WebGL2RenderingContext.RGBA, WebGL2RenderingContext.RGBA, WebGL2RenderingContext.UNSIGNED_BYTE,
			WebGL2RenderingContext.LINEAR_MIPMAP_LINEAR, WebGL2RenderingContext.LINEAR,
			WebGL2RenderingContext.REPEAT, WebGL2RenderingContext.CLAMP_TO_EDGE)
		void this.milkyWayTexture.loadUrl(cMilkyWayTextureUrl)
	}

	public get available(): boolean {
		return this.milkyWayTexture.available
	}
}

class MilkyWayRenderer extends Renderer {
	private cameraForward = new UniformDynamic<vec3>()
	private cameraRight = new UniformDynamic<vec3>()
	private cameraUp = new UniformDynamic<vec3>()
	private tanHalfFov = new UniformDynamic<vec2>()
	private gmst = new UniformDynamic<number>()
	// Bound by the Renderer base class through reflection, hence public
	public intensity = new UniformDynamic<number>(cMilkyWayIntensity)
	private ephemeris: SkyEphemeris

	constructor(context: StovokorContext, geometry: FullscreenTriangle, material: MilkyWayMaterial, ephemeris: SkyEphemeris) {
		super(context, new ShaderSourceDefinition("milkyway"), geometry, material)
		this.ephemeris = ephemeris
		this.drawState.depthTest = false
		this.drawState.depthWrite = false
	}

	protected stateUpdate(state: RenderState): void {
		const camera = state.camera.state
		const forward = normal(camera.forward)
		const right = normal(cross(forward, camera.up))
		const up = cross(right, forward)
		this.cameraForward.value = forward
		this.cameraRight.value = right
		this.cameraUp.value = up

		const projection = state.camera.projection
		const fov = projection instanceof Perspective ? projection.fov : cCameraFOV
		const tanHalf = Math.tan(fov * 0.5 * trig.rad)
		this.tanHalfFov.value = new vec2(tanHalf * state.width / state.height, tanHalf)
		this.gmst.value = this.ephemeris.gmst
	}
}
